/**
 * Posições · Controle de Vencimentos (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_vencimentos.py).
 *
 * Na URL: carteiras e classes (vazio = todas, como o padrão do legado), alerta em dias
 * úteis e "somente com vencimento". As carteiras oferecidas vêm do servidor, já
 * filtradas por `portfolios_visiveis`.
 */
import {
  Alert,
  Checkbox,
  Loader,
  MultiSelect,
  Slider,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/controle-de-vencimentos';

interface Dados {
  opcoes: { carteiras: string[]; conjuntos: string[] };
  info?: string;
  kpis?: KpiApi[];
  tabela?: TabelaApi;
}

const lista = (t: string) => (t ? t.split(',') : []);

export function ControleVencimentos() {
  const [filtros, definir] = useFiltrosUrl({
    carteiras: '',
    conjuntos: '',
    alerta: 15,
    so_vencimento: true as boolean,
  });
  const parametros = {
    carteiras: lista(filtros.carteiras),
    conjuntos: lista(filtros.conjuntos),
    alerta: filtros.alerta,
    so_vencimento: filtros.so_vencimento,
  };
  const consulta = useQuery({
    queryKey: ['carteiras.vencimentos', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<Dados>>(CAMINHO, parametros, signal),
    placeholderData: keepPreviousData,
  });
  const dados = consulta.data?.data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Controle de Vencimentos"
        descricao="Ativos de renda fixa e alternativos que vencem nas carteiras, na posição mais recente de cada uma, em dias úteis até o vencimento."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Fibery (Posição Consolidada, Ativos)"
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Filtros">
          <MultiSelect
            label="Carteiras"
            placeholder={parametros.carteiras.length ? '' : 'Todas'}
            data={dados?.opcoes.carteiras ?? []}
            value={parametros.carteiras}
            onChange={(v) => definir({ carteiras: v.join(',') })}
            searchable
            clearable
            w={LARGURA.selecao + 80}
          />
          <MultiSelect
            label="Classificação do conjunto"
            placeholder={parametros.conjuntos.length ? '' : 'Todas de renda fixa'}
            data={dados?.opcoes.conjuntos ?? []}
            value={parametros.conjuntos}
            onChange={(v) => definir({ conjuntos: v.join(',') })}
            clearable
            w={LARGURA.selecao + 80}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Alerta">
          <Stack gap={4} w={LARGURA.selecao}>
            <Text size="sm" fw={500}>
              Vencimento em menos de {filtros.alerta} dias úteis
            </Text>
            <Slider
              min={1}
              max={90}
              value={filtros.alerta}
              onChangeEnd={(alerta) => definir({ alerta })}
              label={(v) => `${v} dias`}
              color="dourado"
              mt={8}
            />
          </Stack>
          <Checkbox
            label="Somente ativos com data de vencimento"
            checked={filtros.so_vencimento}
            onChange={(e) => definir({ so_vencimento: e.currentTarget.checked })}
            mt={28}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar as posições.'}
        </Alert>
      )}
      {consulta.isPending && <Loader />}
      {dados?.info && <Alert color="blue">{dados.info}</Alert>}
      {dados?.kpis && dados.tabela && (
        <>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            {dados.kpis.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
          <Secao
            titulo="Detalhamento por ativo"
            legenda={`Do vencimento mais próximo ao mais distante. Linhas destacadas: vencimento em menos de ${filtros.alerta} dias úteis.`}
          >
            <TabelaDados tabela={dados.tabela} altura={560} />
          </Secao>
        </>
      )}
    </Pagina>
  );
}
